import { useEffect, useState } from "react";
import api, { fmtIDR } from "@/lib/api";
import { PageHeader, Loading, Empty } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Line, LineChart } from "recharts";
import { toast } from "sonner";
import { Plus, Trash2, Calculator, Wallet, TrendingUp, Users, Warehouse } from "lucide-react";

const yearNow = new Date().getFullYear();
const periodNow = () => new Date().toISOString().slice(0, 7);

const KPI = ({ icon: Icon, label, value, grad }) => (
  <div className={`rounded-xl shadow-sm p-5 text-white ${grad}`}>
    <div className="flex items-center gap-2 mb-2 opacity-90"><Icon className="w-5 h-5" /><span className="font-medium text-sm">{label}</span></div>
    <div className="text-2xl font-semibold font-mono-num">{value}</div>
  </div>
);

export default function NetProfit() {
  const [year, setYear] = useState(yearNow);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [costs, setCosts] = useState([]);
  const [form, setForm] = useState({ period: periodNow(), category: "Sewa Toko", amount: 0, note: "" });

  const load = () => {
    setLoading(true);
    api.get("/reports/net-profit", { params: { year } }).then(({ data }) => setData(data)).finally(() => setLoading(false));
    api.get("/operational-costs").then(({ data }) => setCosts(data)).catch(() => {});
  };
  useEffect(() => { load(); }, [year]);

  const addCost = async () => {
    if (!form.amount) return toast.error("Isi nominal biaya");
    try {
      await api.post("/operational-costs", { period: form.period, category: form.category, amount: Number(form.amount), note: form.note });
      toast.success("Biaya operasional ditambahkan"); setForm({ ...form, amount: 0, note: "" }); load();
    } catch { toast.error("Gagal"); }
  };
  const delCost = async (c) => { await api.delete(`/operational-costs/${c.id}`); load(); };

  const t = data?.totals || {};

  return (
    <div>
      <PageHeader title="Laporan Laba Rugi Bersih" subtitle="Laba bersih = (Laba Penjualan + Servis + PPOB) - (Gaji + Biaya Operasional)">
        <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
          <SelectTrigger className="w-32" data-testid="year-select"><SelectValue /></SelectTrigger>
          <SelectContent>{[yearNow, yearNow - 1, yearNow - 2].map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
        </Select>
      </PageHeader>

      {loading ? <Loading /> : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            <KPI icon={TrendingUp} label="Total Laba Kotor" value={fmtIDR(t.gross)} grad="bg-gradient-to-br from-sky-600 to-sky-700" />
            <KPI icon={Users} label="Total Gaji" value={fmtIDR(t.salary)} grad="bg-gradient-to-br from-amber-500 to-amber-600" />
            <KPI icon={Wallet} label="Total Operasional" value={fmtIDR(t.operational)} grad="bg-gradient-to-br from-red-500 to-red-600" />
            <KPI icon={Calculator} label="Laba Bersih Tahun" value={fmtIDR(t.net)} grad="bg-gradient-to-br from-green-600 to-green-700" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
            <div className="lg:col-span-2 bg-white rounded-xl border border-slate-200 shadow-sm p-5">
              <h3 className="font-medium text-slate-800 mb-4">Tren Laba Bersih {year}</h3>
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={data.months}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="month" tick={{ fontSize: 10 }} tickFormatter={(v) => v.slice(5)} />
                  <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `${Math.round(v / 1000)}k`} width={48} />
                  <Tooltip formatter={(v) => fmtIDR(v)} />
                  <Line type="monotone" dataKey="net_profit" stroke="#16a34a" strokeWidth={2} dot={{ r: 3 }} name="Laba Bersih" />
                  <Line type="monotone" dataKey="gross_profit" stroke="#0284c7" strokeWidth={2} dot={false} name="Laba Kotor" />
                </LineChart>
              </ResponsiveContainer>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
              <div className="flex items-center gap-2 mb-3"><Warehouse className="w-4 h-4 text-sky-600" /><h3 className="font-medium text-slate-800">Nilai Aset Toko</h3></div>
              <div className="text-2xl font-semibold font-mono-num text-sky-700" data-testid="asset-total">{fmtIDR(data.asset_total)}</div>
              <p className="text-xs text-slate-400 mt-1">Total nilai seluruh aset toko (cabang aktif).</p>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 mb-4">
            <div className="flex items-center gap-2 mb-3"><Plus className="w-4 h-4 text-sky-600" /><h3 className="font-medium text-slate-800">Input Biaya Operasional Bulanan</h3></div>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
              <div><Label className="text-xs text-slate-500">Periode</Label><Input type="month" value={form.period} onChange={(e) => setForm({ ...form, period: e.target.value })} className="mt-1" data-testid="opcost-period" /></div>
              <div>
                <Label className="text-xs text-slate-500">Kategori</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger className="mt-1" data-testid="opcost-category"><SelectValue /></SelectTrigger>
                  <SelectContent>{["Sewa Toko", "Listrik", "WiFi / Internet", "Air", "Gaji Non-Payroll", "Biaya Tak Terduga", "Lainnya"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs text-slate-500">Nominal</Label><Input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="mt-1" data-testid="opcost-amount" /></div>
              <div><Label className="text-xs text-slate-500">Catatan</Label><Input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className="mt-1" /></div>
              <Button onClick={addCost} className="bg-sky-600 hover:bg-sky-700" data-testid="opcost-add">Tambah</Button>
            </div>
            <div className="mt-4 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-y border-slate-200"><tr>
                  {["Periode", "Kategori", "Nominal", "Catatan", ""].map((h) => <th key={h} className="px-3 py-2 text-left font-medium text-slate-500">{h}</th>)}
                </tr></thead>
                <tbody className="divide-y divide-slate-100">
                  {costs.length === 0 ? <tr><td colSpan={5}><Empty text="Belum ada biaya operasional" /></td></tr>
                    : costs.map((c) => (
                      <tr key={c.id}>
                        <td className="px-3 py-2">{c.period}</td>
                        <td className="px-3 py-2">{c.category}</td>
                        <td className="px-3 py-2 font-mono-num text-red-600">{fmtIDR(c.amount)}</td>
                        <td className="px-3 py-2 text-slate-500">{c.note}</td>
                        <td className="px-3 py-2 text-right"><button onClick={() => delCost(c)} className="p-1.5 text-slate-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button></td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200"><tr>
                {["Bulan", "Laba Jual", "Laba Servis", "Laba PPOB", "Laba Kotor", "Gaji", "Operasional", "Laba Bersih"].map((h) => <th key={h} className="px-4 py-3 text-left font-medium text-slate-500">{h}</th>)}
              </tr></thead>
              <tbody className="divide-y divide-slate-100">
                {data.months.map((m) => (
                  <tr key={m.month} className="hover:bg-slate-50">
                    <td className="px-4 py-3">{m.month}</td>
                    <td className="px-4 py-3 font-mono-num">{fmtIDR(m.sales_profit)}</td>
                    <td className="px-4 py-3 font-mono-num">{fmtIDR(m.service_profit)}</td>
                    <td className="px-4 py-3 font-mono-num">{fmtIDR(m.ppob_profit)}</td>
                    <td className="px-4 py-3 font-mono-num text-sky-700">{fmtIDR(m.gross_profit)}</td>
                    <td className="px-4 py-3 font-mono-num text-amber-600">{fmtIDR(m.salary)}</td>
                    <td className="px-4 py-3 font-mono-num text-red-600">{fmtIDR(m.operational)}</td>
                    <td className={`px-4 py-3 font-mono-num font-semibold ${m.net_profit >= 0 ? "text-green-600" : "text-red-600"}`}>{fmtIDR(m.net_profit)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
